/**
 * تنفيذ محلي لتخزين الملفات داخل IndexedDB.
 * الروابط المؤقتة تُنشأ في الذاكرة (object URL) وتُلغى تلقائيًا بعد انتهاء المدة.
 */

import type { BlobStore } from '../blob-store';
import { getLocalDb, type FileBlob } from './db';
import { newId } from '@/domain/ids';

export class LocalBlobStore implements BlobStore {
  private readonly urls = new Map<string, { url: string; timer: ReturnType<typeof setTimeout> }>();

  async put(path: string, bytes: ArrayBuffer, mimeType: string): Promise<void> {
    const db = await getLocalDb();
    const existing = (await db.getFromIndex('file_blobs', 'storagePath', path)) as FileBlob | undefined;
    await db.put('file_blobs', {
      id: existing?.id ?? newId(),
      storagePath: path,
      bytes,
      mimeType,
      updatedAt: new Date().toISOString(),
    });
  }

  async get(path: string): Promise<ArrayBuffer | null> {
    const db = await getLocalDb();
    const record = (await db.getFromIndex('file_blobs', 'storagePath', path)) as FileBlob | undefined;
    return record?.bytes ?? null;
  }

  async exists(path: string): Promise<boolean> {
    const db = await getLocalDb();
    const record = await db.getFromIndex('file_blobs', 'storagePath', path);
    return record !== undefined;
  }

  async remove(path: string): Promise<void> {
    const db = await getLocalDb();
    const record = (await db.getFromIndex('file_blobs', 'storagePath', path)) as FileBlob | undefined;
    if (record) await db.delete('file_blobs', record.id);
    const cached = this.urls.get(path);
    if (cached) {
      URL.revokeObjectURL(cached.url);
      clearTimeout(cached.timer);
      this.urls.delete(path);
    }
  }

  async createUrl(path: string, expiresInSeconds: number): Promise<string> {
    const bytes = await this.get(path);
    if (!bytes) throw new Error('الملف غير موجود في التخزين المحلي');
    const db = await getLocalDb();
    const record = (await db.getFromIndex('file_blobs', 'storagePath', path)) as FileBlob | undefined;
    const blob = new Blob([bytes], { type: record?.mimeType ?? 'application/octet-stream' });
    const previous = this.urls.get(path);
    if (previous) {
      URL.revokeObjectURL(previous.url);
      clearTimeout(previous.timer);
    }
    const url = URL.createObjectURL(blob);
    const timer = setTimeout(
      () => {
        URL.revokeObjectURL(url);
        this.urls.delete(path);
      },
      Math.max(5, expiresInSeconds) * 1000,
    );
    this.urls.set(path, { url, timer });
    return url;
  }

  /** إلغاء كل الروابط عند تسجيل الخروج */
  dispose(): void {
    for (const { url, timer } of this.urls.values()) {
      URL.revokeObjectURL(url);
      clearTimeout(timer);
    }
    this.urls.clear();
  }
}
